import * as Sentry from '@sentry/react'
import { onAuthStateChanged, getAuth, User, AuthCredential, linkWithCredential, onIdTokenChanged } from 'firebase/auth'
import { ReactNode, useState, useEffect, useRef, useMemo } from 'react'
import firebase_app from '../firebase/firebase-config'
import FullPageLoader from '@/components/loaders/FullPageLoader'
import { setSentryUser } from '@/util/sentry/set-sentry-user'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { FirebaseError } from 'firebase/app'
import { UserAccountData } from '@/firebase/auth/user-account-data'
import ErrorComponent from '@/components/error/error-component'
import createStripeCustomer from '@/chat-common/fetch/fetch-create-customer'
import { InferredPollingState, PostSignupPhoneStatus } from '@/firebase/auth/auth-jwt-schema'
import { fetchOrgLogoAsUrl } from '@/organizations/services/org-logo-services'
import { Userpilot } from 'userpilot'
import { clearReduxStateData } from '@/firebase/auth/clear-user-data'
import { forgetSignedInUser, hadSignedInUser, rememberSignedInUser } from '@/firebase/auth/signed-in-marker'
import { useAppDispatch } from '@/store/store-hooks'
import { setSessionEndedModalVisible } from '@/store/slices/ui-state.slice'
import { AuthContext } from './auth-context'
import { resolveUserIdentity, type UserProfileSnapshot } from '@/account/utils/resolve-user-identity'
import { kDefaultLogoPath } from '@/constants/constants-ui'
import { kFeatureFlags, kGetFeatureFlagStatus } from '@/constants/constants-feature-flags'
import { clearAttachedSkillNames } from '@/user-skills/utils/attached-skill-storage'
import { clearAllRecentSearches } from '@/user_files/search/recent-searches-storage'
import { kSessionStorageActiveMatterIdKey } from '@/matter/constants'
import { kSessionStorageSubscriptionNoticeBannerDismissedKey } from '@/account/constants'
import { clearOutlookLocalData } from '@/outlook-addin/auth/logout'

const accountSetupUxErrorMessage = 'Unable to initialize your account information. Please try again.'
const auth = getAuth(firebase_app)

function isRequiredPhoneCollectionInProgress(userAccountData: UserAccountData | null): boolean {
  return Boolean(
    kGetFeatureFlagStatus(kFeatureFlags.POST_SIGNUP_PHONE_COLLECTION) &&
      userAccountData &&
      !userAccountData.isAnonymous &&
      !userAccountData.isOrgAccount &&
      userAccountData.postSignupPhoneStatus === PostSignupPhoneStatus.REQUIRED
  )
}

export const AuthContextProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null)
  const [updated, setUpdated] = useState<string | null>(null)
  const [logoUrl, setLogoUrl] = useState<string>(kDefaultLogoPath)
  const [loading, setLoading] = useState(true) // Always start as loading
  const [pollingStarted, setPollingStarted] = useState(false)
  const [subscriptionPollingPaused, setSubscriptionPollingPaused] = useState(false)
  const [authContextError, setAuthContextError] = useState<string | null>(null)
  const [userAccountData, setUserAccountData] = useState<UserAccountData | null>(null)
  // Canonical profile snapshot. Stays null until a canonical read source is wired (FE-771) or the
  // profile editor saves (FE-772); while null, identity derives from the transitional Firebase
  // fallback below. This is the single seam the profile editor replaces.
  const [userProfile, setUserProfile] = useState<UserProfileSnapshot | null>(null)
  const credentialLinkInProgressRef = useRef(false)
  const previousObservedUidRef = useRef<string | null>(null)
  const subscriptionPollingPausedRef = useRef(false)
  const dispatch = useAppDispatch()
  const { trackEvent } = useAnalytics()

  // Tracks the current logo object URL so we can revoke the previous blob when swapping.
  // `fetchOrgLogoAsUrl` mints a fresh `URL.createObjectURL` per call, so replacing without
  // revoking would leak one blob per refresh for the page's lifetime. Bundled default logo
  // paths are plain URLs (not object URLs) and must never be revoked.
  const logoObjectUrlRef = useRef<string>(kDefaultLogoPath)

  const applyOrgLogoUrl = (nextLogoUrl: string) => {
    const previousLogoUrl = logoObjectUrlRef.current
    if (previousLogoUrl !== nextLogoUrl && previousLogoUrl !== kDefaultLogoPath) {
      URL.revokeObjectURL(previousLogoUrl)
    }
    logoObjectUrlRef.current = nextLogoUrl
    setLogoUrl(nextLogoUrl)
  }

  /**
   * Clear Auth State Global and Context
   * This function is used to clear the global state and the context state.
   */
  const clearAuthStateGlobalAndContext = (props: { loading: boolean }) => {
    const { loading } = props

    // Global state updates:
    clearReduxStateData()

    // Context State updates
    setLoading(loading)
    setUser(null)
    setUserAccountData(null)
    setUserProfile(null)
    setPollingStarted(false)
    subscriptionPollingPausedRef.current = false
    setSubscriptionPollingPaused(false)
  }

  // INITIAL LOAD
  // *** MUST RUN ALL CRITICAL NEW USER AUTH PROCESSES ***
  // Update the user object when the auth state changes (login / logout / user change)
  // DOES NOT RUN on linkWithCredential
  useEffect(() => {
    setAuthContextError(null)

    const unsubscribe = onAuthStateChanged(auth, async (authChangeUser) => {
      const previousObservedUid = previousObservedUidRef.current
      const nextUid = authChangeUser?.uid ?? null
      previousObservedUidRef.current = nextUid

      // Another tab can observe a replacement identity without an intermediate null event.
      if (!authChangeUser || (previousObservedUid !== null && previousObservedUid !== nextUid)) {
        clearAttachedSkillNames()
        clearAllRecentSearches()
        clearOutlookLocalData()
        // A logout observed from another tab does not run this tab's explicit sign-out helper.
        try {
          sessionStorage.removeItem(kSessionStorageActiveMatterIdKey)
          sessionStorage.removeItem(kSessionStorageSubscriptionNoticeBannerDismissedKey)
        } catch (error) {
          Sentry.captureException(error)
        }
      }

      // User is null condition (logout has occurred)
      if (!authChangeUser) {
        console.log('Cleaning up after logout...')

        // Global and context auth states
        clearAuthStateGlobalAndContext({ loading: false })

        // A remembered user that signOutOfApp did not forget had its session revoked elsewhere.
        if (hadSignedInUser()) dispatch(setSessionEndedModalVisible(true))
        forgetSignedInUser()
        return
      }

      if (authChangeUser.isAnonymous) forgetSignedInUser()
      else rememberSignedInUser(authChangeUser.uid)

      // User has changed check
      const userHasChanged = user?.uid !== authChangeUser.uid

      // If the user has changed, we need to clear the application state
      if (userHasChanged) {
        console.log('User change detected.')

        // Global and context auth states
        clearAuthStateGlobalAndContext({ loading: true })
      }

      // Initialize the user subscription data
      try {
        const userAccountData = await UserAccountData.create(authChangeUser)
        if (!userAccountData) {
          Sentry.captureException(new Error('onAuthStateChanged: No UserAccountData after UserAccountData.create'), {
            extra: {
              user: JSON.stringify(authChangeUser),
              uxErrorMessage: accountSetupUxErrorMessage,
              userAccountData: JSON.stringify(userAccountData),
            },
          })
          setAuthContextError(accountSetupUxErrorMessage)
          return
        }

        // Fetch the org logo
        const orgId = userAccountData.legacyDriveCompatibleOrganizationId
        const logoUrl = await fetchOrgLogoAsUrl(orgId)

        // Update state
        setUserAccountData(userAccountData)
        // A transient fetch failure resolves to null here; fall back to the default (unchanged
        // load-time behavior). The null-aware refresh path is what avoids the false deletion.
        applyOrgLogoUrl(logoUrl ?? kDefaultLogoPath)
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            exception: JSON.stringify(e),
            uxErrorMessage: accountSetupUxErrorMessage,
            detail: 'Error constructing user account data during onAuthStateChanged',
            userAccountData: JSON.stringify(userAccountData),
          },
        })
        setAuthContextError(accountSetupUxErrorMessage)
      }

      // Update the user state
      setUser(authChangeUser)

      // Set updated (force re-render)
      setUpdated(Date.now().toString())

      // Tag user for Sentry
      setSentryUser(authChangeUser)

      // Set loading
      setLoading(false)
    })

    return () => unsubscribe()
  }, [])

  // ID TOKEN REFRESH TRIGGER
  // When the user id token is refreshed, ensure we update the auth context for all listeners
  useEffect(() => {
    const unsubscribeTokenRefresh = onIdTokenChanged(auth, async (tokenUser) => {
      if (tokenUser) {
        Userpilot.identify(tokenUser.uid, {
          email: tokenUser.email,
        })

        // Linking mutates the same user before account data is ready; linkCredential publishes both together.
        if (credentialLinkInProgressRef.current) return

        // A token refresh can update the current user, but an identity transition belongs to
        // onAuthStateChanged so account hydration completes before the new user is published.
        setUser((publishedUser) => (publishedUser?.uid === tokenUser.uid && publishedUser.isAnonymous === tokenUser.isAnonymous ? tokenUser : publishedUser))

        // Set updated (force re-render)
        setUpdated(Date.now().toString())
      }
    })

    return () => unsubscribeTokenRefresh()
  }, [])

  // UPDATED TRIGGER
  // When Auth State Updated
  // Check for and poll for pending subscription
  useEffect(() => {
    if (!user) return
    if (!userAccountData) return

    const phoneCollectionInProgress = isRequiredPhoneCollectionInProgress(userAccountData)

    if (phoneCollectionInProgress && pollingStarted && !subscriptionPollingPaused) {
      subscriptionPollingPausedRef.current = true
      setSubscriptionPollingPaused(true)
      return
    }

    if (subscriptionPollingPaused) {
      if (!phoneCollectionInProgress) {
        subscriptionPollingPausedRef.current = false
        setSubscriptionPollingPaused(false)

        if (userAccountData.inferredPollingState == InferredPollingState.POLL_SUBSCRIPTION) {
          void refreshUserAccountDataForSubscription(Date.now(), user)
        }
      }
      return
    }

    if (!pollingStarted && userAccountData.inferredPollingState == InferredPollingState.POLL_SUBSCRIPTION) {
      void pollWhilePendingSubscription(user, userAccountData)
    }
  }, [user, userAccountData, updated, pollingStarted, subscriptionPollingPaused])

  /**
   * Refresh Org Logo
   * Re-fetches the organization logo and updates the context so consumers (e.g. the member
   * roster modal) reflect a newly uploaded logo without a full page reload.
   */
  const refreshOrgLogo = async (): Promise<boolean> => {
    const orgId = userAccountData?.legacyDriveCompatibleOrganizationId ?? null
    const refreshedLogoUrl = await fetchOrgLogoAsUrl(orgId)
    if (refreshedLogoUrl === null) {
      // Transient fetch failure — keep the current logo rather than falsely swapping to the
      // default (which the UI would read as a deletion). Let the caller surface a toast.
      return false
    }
    applyOrgLogoUrl(refreshedLogoUrl)
    return true
  }

  /**
   * Refresh User Account and Account Data
   * This function can be manually triggered from anywhere that has access to the AuthContext
   * in order to refresh the user's Firebase account (JWT) and the user account data object.
   * @returns
   */
  const refreshUserAccountData = async (): Promise<UserAccountData | null> => {
    if (!user) return null
    if (!userAccountData) return null

    // Get the current user
    const currentUser = auth.currentUser
    if (currentUser == null) throw new Error('Cannot refresh user account data for a null user.')

    try {
      // Refresh the Firebase user and their JWT
      await currentUser.reload()

      const refreshedUserAccountData = await UserAccountData.create(currentUser)

      // The refreshed account data is untrustworthy in two cases, and in both we keep the last good
      // data instead of overwriting it:
      //  1. create() returned null — a transient token-refresh failure. Overwriting with null would
      //     wedge AccountBootstrapGuard on a loader and make every later refresh early-exit (the
      //     `if (!userAccountData) return null` guard above), so the next poll could never recover.
      //  2. An org member whose getOrganization() failed, so orgData is null. Keeping that would drop
      //     the org's has_access signal and fall back to the member's individual subscription claim,
      //     reopening an expired org's dashboard. (create() stays usable for the initial-load and
      //     subscription-poll callers, which must not hard-fail on a transient org-fetch miss.)
      // get-organization.ts already reports the underlying failure to Sentry. The next poll retries.
      const orgDataMissing = Boolean(refreshedUserAccountData?.isOrgAccount) && !refreshedUserAccountData?.orgData
      if (!refreshedUserAccountData || orgDataMissing) {
        console.warn('refreshUserAccountData: refreshed account data is unusable (null or missing org data); keeping prior account data')
        return null
      }

      setUserAccountData(refreshedUserAccountData)
      setUser(currentUser)
      return refreshedUserAccountData
    } catch (e) {
      // Error will be filtered by Sentry beforeSend if it's an expected Firebase error
      // Unexpected errors will still be reported naturally
      // Background refresh can fail gracefully - app continues with current data
      console.error('Error refreshing user account data:', e)
      return null
    }
  }

  // Link credential
  // *** MUST RUN ALL CRITICAL NEW USER AUTH PROCESSES ***
  // Link a credential to the current anonymous user
  // Converts an anonymous firebase account to a permanent account, maintaining the existing user id
  const linkCredential = async (credential: AuthCredential) => {
    setAuthContextError(null)

    const currentUser = auth.currentUser
    if (currentUser == null) throw new Error(`Cannot convert ${credential.signInMethod} from a null user.`)

    console.log('Linking credential of type:', credential.signInMethod)

    credentialLinkInProgressRef.current = true

    try {
      const linkedUserCredential = await linkWithCredential(currentUser, credential)
      // console.log('Linked user credential: ', linkedUserCredential)

      // Initialize the user subscription data
      try {
        const userAccountData = await UserAccountData.create(linkedUserCredential.user)
        if (!userAccountData) {
          Sentry.captureException(new Error('linkCredential: No UserAccountData after UserAccountData.create'), {
            extra: {
              user: JSON.stringify(user),
              uxErrorMessage: accountSetupUxErrorMessage,
              userAccountData: JSON.stringify(userAccountData),
            },
          })
          setAuthContextError(accountSetupUxErrorMessage)
          return
        }

        trackEvent(AnalyticsEvent.SignUp)

        // Fetch the org logo
        const orgId = userAccountData.legacyDriveCompatibleOrganizationId
        const logoUrl = await fetchOrgLogoAsUrl(orgId)

        // Update state
        setUserAccountData(userAccountData)
        // A transient fetch failure resolves to null here; fall back to the default (unchanged
        // load-time behavior). The null-aware refresh path is what avoids the false deletion.
        applyOrgLogoUrl(logoUrl ?? kDefaultLogoPath)
      } catch (e) {
        Sentry.captureException(e, {
          extra: {
            exception: JSON.stringify(e),
            uxErrorMessage: accountSetupUxErrorMessage,
            detail: 'Error constructing user account data during linkCredential',
            userAccountData: JSON.stringify(userAccountData),
          },
        })
        setAuthContextError(accountSetupUxErrorMessage)
      }

      // Update the user
      setUser(linkedUserCredential.user)

      // Set updated (force re-render)
      setUpdated(Date.now().toString())

      // Tag user for Sentry
      setSentryUser(linkedUserCredential.user)
    } catch (e) {
      if (e instanceof FirebaseError) {
        // Email already registered, this is handled gracefully in the UX, throw to calling ux function
        if (e.code == 'auth/email-already-in-use' && credential.signInMethod == 'password') {
          throw e
        }
      }

      // Other error, should record to sentry
      Sentry.captureException(new Error(`Error linking credential. Method: ${credential.signInMethod}`), {
        extra: {
          credential: JSON.stringify(credential.toJSON()),
        },
      })
      throw e
    } finally {
      credentialLinkInProgressRef.current = false
    }
  }

  const refreshUserAccountDataForSubscription = async (startTime: number, user: User): Promise<void> => {
    console.log('Starting refreshUserAccountDataForSubscription...')

    if (subscriptionPollingPausedRef.current) return

    if (Date.now() - startTime > 20000) {
      Sentry.captureException(new Error('Subscription polling exceeded 20 seconds'), {
        extra: {
          uxErrorMessage: accountSetupUxErrorMessage,
          detail: 'UX Error shown to user, refreshUserAccountDataForSubscription() took longer than 20 seconds',
          userAccountData: JSON.stringify(userAccountData),
        },
      })
      setAuthContextError(accountSetupUxErrorMessage)
      return
    }

    try {
      console.log('Polling for update to pending subscription...')
      const refreshedSubscriptionData = await UserAccountData.create(user)

      if (isRequiredPhoneCollectionInProgress(refreshedSubscriptionData)) {
        subscriptionPollingPausedRef.current = true
        setUserAccountData(refreshedSubscriptionData)
        setSubscriptionPollingPaused(true)
        return
      }

      // If still a pending subscription, try again
      if (refreshedSubscriptionData?.pendingSubscription) {
        await new Promise((resolve) => setTimeout(resolve, 2000))
        return refreshUserAccountDataForSubscription(startTime, user)
      }

      // Subscription is valid
      console.log('Subscription no longer pending: ', refreshedSubscriptionData?.subscriptionStatus)

      // Refresh the user object JWT and reload the user
      await user.getIdToken(true)
      await user.reload()

      // Update the user and subscription state, and trigger the update
      setUser(user)
      setUserAccountData(refreshedSubscriptionData)
      setUpdated(Date.now().toString())

      return
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          exception: JSON.stringify(e),
          uxErrorMessage: accountSetupUxErrorMessage,
          detail: 'UX Error shown to user, there was an exception executing refreshUserAccountDataForSubscription()',
          userAccountData: JSON.stringify(userAccountData),
        },
      })
      setAuthContextError(accountSetupUxErrorMessage)
    }
  }

  // Poll While Pending Subscription (userAccountData?.pendingSubscription)
  // Updates state whenever the subscription status changes to non-pending
  // Throws an error if polling for more than 20 seconds
  const pollWhilePendingSubscription = async (user: User, userAccountData: UserAccountData) => {
    // Not needed for anon users or users not yet email verified (that guard will block them)
    if (user.isAnonymous || userAccountData.isAnonymous) {
      return
    }

    // If the subscription is valid, we don't need to poll, return early
    if (userAccountData?.validSubscription) {
      return
    }

    // If user subscription is not pending, we don't need to poll, return early
    if (userAccountData?.pendingSubscription == false) {
      return
    }

    // Set the polling started flag
    setPollingStarted(true)

    // Request customer + subscription setup
    // Pending subscription means the user has not yet completed the subscription setup
    try {
      await createStripeCustomer()
      await new Promise((resolve) => setTimeout(resolve, 3000))
    } catch (e) {
      Sentry.captureException(e, {
        extra: {
          exception: JSON.stringify(e),
          uxErrorMessage: accountSetupUxErrorMessage,
          detail: 'Error creating customer in pollWhilePendingSubscription',
          userAccountData: JSON.stringify(userAccountData),
        },
      })
      setAuthContextError(accountSetupUxErrorMessage)
    }

    // Recursive function that continues to refresh a new UserAccountDataData object until the user has a valid subscription
    const startTime = Date.now()

    // Start the polling
    refreshUserAccountDataForSubscription(startTime, user)
  }

  // Derive the navigation identity from the canonical snapshot when present, otherwise from the
  // transitional Firebase fallback. When the snapshot is null the name fields are left `undefined`
  // (canonical-not-loaded), which is what permits the Firebase `displayName` fallback.
  const userIdentity = useMemo(
    () =>
      resolveUserIdentity(
        userProfile
          ? {
              firstName: userProfile.firstName,
              lastName: userProfile.lastName,
              email: userProfile.email ?? user?.email ?? null,
              firebaseDisplayName: user?.displayName ?? null,
            }
          : {
              email: user?.email ?? null,
              firebaseDisplayName: user?.displayName ?? null,
            }
      ),
    [userProfile, user?.email, user?.displayName]
  )

  return (
    <AuthContext.Provider
      value={{
        user,
        updated,
        loading,
        logoUrl,
        refreshOrgLogo,
        linkCredential,
        userAccountData,
        refreshUserAccountData,
        clearAuthStateGlobalAndContext,
        pollingStarted,
        userIdentity,
        setUserProfile,
      }}
    >
      {/* Handle authentication errors */}
      {authContextError && (
        <ErrorComponent
          code="500"
          title="Authentication Error"
          message={'There was an error authenticating with Paxton. Support has been notified.'}
          showRefreshButton={true}
        />
      )}

      {/* Show full page loader while loading, or the subscription is pending otherwise show children */}
      {!authContextError && loading && <FullPageLoader />}

      {/* Otherwise, return the children */}
      {!authContextError && !loading && children}
    </AuthContext.Provider>
  )
}
